import React from 'react'
import clsx from 'clsx'
import IconWrapper from '@/shared/components/UI/IconWrapper'
import DSpinner from '@/components/Global/DSpinner'

const Button = ({
  icon: Icon, // Legacy prop for backward compatibility (acts as iconBefore)
  iconAfter,
  iconClassName = '', // Custom className for the icon
  label = '',
  onClick,
  className = '',
  disabled = false,
  loading = false,
  visible = true,
  variant = 'primary',
  config = {},
  loadingLabel = '',
  size = 'lg',
  iconColor = 'text-white',
  spinnerColor = '',
  spinnerClassName = '',
  ...props
}) => {
  if (!visible) return null

  const handleClick = (event) => {
    if (disabled || loading) return
    if (onClick) {
      onClick(event)
    }
  }

  // Use iconBefore if provided, otherwise fall back to legacy icon prop
  const beforeIcon = Icon
  const afterIcon = iconAfter

  // Helper to check if icon is a string and a link (http/https or data url)
  const isIconUrl = (icon) => typeof icon === 'string' && (
    icon.startsWith('http://') ||
    icon.startsWith('https://') ||
    icon.startsWith('data:')
  )

  const isSecondary = variant === 'secondary'
  const isTransparent = variant === 'transparent'
  const isFilter = variant === 'filter'
  const isDanger = variant === 'danger'
  const isDangerSecondary = variant === 'danger-secondary'
  const isWarning = variant === 'warning'
  const isSuccess = variant === 'success'
  const isFloating = variant === 'floating'
  const isDark = variant === 'dark'
  const isGold = variant === 'gold'

  // Size mapping for button heights
  const sizeClasses = {
    xs: 'h-v3-ml',
    sm: 'h-v3-lg',
    md: 'h-[36px]',
    lg: 'h-[40px]',
    xl: 'h-v3-xxl',
  }

  // Size mapping for button padding
  const getPaddingClasses = (size, hasBeforeIcon) => {
    // Floating and dark variants have specific padding: 12px 16px 12px 12px
    if ((isFloating || isDark) && hasBeforeIcon) {
      return 'py-v3-ms pl-v3-ms pr-v3-sm' // 12px top/bottom, 12px left, 16px right
    }
    
    const basePadding = {
      sm: hasBeforeIcon ? 'py-v3-xm pl-v3-ms pr-v3-sm' : 'py-v3-xm px-v3-sm', // 12px left when icon, 16px otherwise
      md: hasBeforeIcon ? 'py-v3-xm pl-v3-ms pr-v3-sm' : 'py-v3-xm px-v3-sm', // 12px left when icon, 16px otherwise
      lg: hasBeforeIcon ? 'py-v3-xm pl-v3-sm pr-v3-md' : 'py-v3-xm px-v3-md', // 12px left when icon, 24px otherwise
      xl: hasBeforeIcon ? 'py-v3-xm pl-v3-sm pr-v3-md' : 'py-v3-xm px-v3-md', // 12px left when icon, 24px otherwise
    }
    return basePadding[size] || basePadding.md
  }

  const buttonHeight = (isFloating || isDark) ? 'h-auto' : (sizeClasses[size] || sizeClasses.md)
  const buttonPadding = getPaddingClasses(size, !!beforeIcon)

  return (
    <button
      onClick={handleClick}
      disabled={disabled || loading}
      className={clsx(
        'dashboard-button',
        'w-fit',
        'group',
        buttonHeight,
        'flex',
        'items-center',
        (isFloating || isDark) ? 'gap-1' : 'gap-v3-xxs', // 4px gap for floating and dark variants
        buttonPadding,
        'rounded-v3-md',
        !isFloating && !isDark && !isFilter && 'border',
        !isFilter && 'text-v3-h9',
        !isFilter && 'font-medium',
        isFilter && 'text-v3-h11 font-medium text-center',
        'transition-all',
        'duration-200',
        'active:scale-95',
        'disabled:cursor-not-allowed',
        // Floating variant styles
        isFloating && [
          'bg-v3-black-max',
          'text-white',
          'hover:bg-v3-black-dark',
          'disabled:bg-v3-black-placeholder',
          'disabled:text-v3-black-placeholder',
        ],
        // Dark variant styles
        isDark && [
          'bg-v3-black-darker',
          'text-white',
          'hover:bg-v3-purple-lightest',
          'hover:text-v3-black-max',
          'disabled:bg-v3-black-placeholder',
          'disabled:text-v3-black-placeholder',
        ],
        // Primary variant styles
        !isSecondary && !isTransparent && !isFilter && !isDanger && !isDangerSecondary && !isWarning && !isSuccess && !isFloating && !isDark && !isGold && [
          'bg-v3-purple-primary',
          'border-v3-purple-primary',
          'text-white',
          'hover:bg-v3-purple-medium',
          'hover:border-v3-purple-medium',
          'disabled:bg-v3-black-placeholder',
          'disabled:border-v3-black-placeholder',
          'disabled:hover:bg-v3-black-placeholder',
          'disabled:hover:border-v3-black-placeholder',
        ],
        // Secondary variant styles
        isSecondary && [
          'bg-v3-purple-lightest',
          'border-v3-purple-lightest',
          'text-v3-purple-primary',
          'hover:bg-v3-purple-lighter',
          'hover:border-v3-purple-lighter',
          'disabled:hover:bg-v3-purple-lightest',
          'disabled:hover:border-v3-purple-lightest',
        ],
        // Transparent variant styles
        isTransparent && [
          'bg-transparent',
          'border-none',
          'text-v3-purple-primary',
          'hover:text-v3-purple-medium',
          'disabled:hover:text-v3-purple-primary',
          'text-v3-h10',
          'gap-v3-xs'
        ],
        // Filter variant styles
        isFilter && [
          'bg-transparent',
          'border-none',
          'text-v3-black-max',
          'font-inter-variable',
          'hover:bg-v3-foreground-2',
          'disabled:hover:bg-transparent',
          'gap-v3-xs'
        ],
        // Danger variant styles
        isDanger && [
          'bg-v3-red-dark',
          'border-v3-red-dark',
          'text-white',
          'hover:bg-v3-red-primary',
          'hover:border-v3-red-primary',
          'disabled:bg-v3-black-placeholder',
          'disabled:border-v3-black-placeholder',
          'disabled:hover:bg-v3-black-placeholder',
          'disabled:hover:border-v3-black-placeholder',
        ],
        // Danger-secondary variant styles
        isDangerSecondary && [
          'bg-v3-red-light',
          'border-v3-red-light',
          'text-v3-red-dark',
          'hover:bg-v3-red-lighter',
          'hover:border-v3-red-lighter',
          'hover:text-v3-red-dark',
            'disabled:hover:bg-v3-black-placeholder',
          'disabled:hover:border-v3-black-placeholder',
        ],
        // Warning variant styles
        isWarning && [
          'bg-v3-warning-primary',
          'border-v3-warning-primary',
          'text-white',
          'hover:bg-v3-orange-dark',
          'hover:border-v3-orange-dark',
          'disabled:bg-v3-black-placeholder',
          'disabled:border-v3-black-placeholder',
          'disabled:hover:bg-v3-black-placeholder',
          'disabled:hover:border-v3-black-placeholder',
        ],
        // Success variant styles
        isSuccess && [
          'bg-v3-green-dark',
          'text-white',
        ],
        // Gold variant styles (Pro plan)
        isGold && [
          'text-white',
          'border-transparent',
          'hover:opacity-90',
          'disabled:opacity-50',
          'disabled:hover:opacity-50',
        ],
        className
      )}
      style={{
        fontFamily: config?.font_name ? `${config.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
        fontSize: config?.font_size ? `${config.font_size}px` : undefined,
        ...(isGold && {
          background: 'linear-gradient(94deg, #D99606 0.21%, #D07F06 100.21%)',
          borderColor: 'transparent',
        }),
      }}
      {...props}
    >
      {!loading && beforeIcon && (
        isIconUrl(beforeIcon) ? (
          <img src={beforeIcon} className="flex-shrink-0 w-5 h-5" alt="icon" />
        ) : (
          typeof beforeIcon === 'function' ? (
            <IconWrapper size={20}>
              {React.createElement(beforeIcon, {
                className: clsx(
                  iconClassName || (
                    isDanger ? 'text-white' :
                    isDangerSecondary ? 'text-v3-red-dark' :
                    isSuccess ? 'text-white' :
                    isFloating ? 'text-white' :
                    isDark ? 'text-white' :
                    isGold ? 'text-white' :
                    isFilter ? 'text-v3-black-max' :
                    isTransparent || isSecondary ? 'text-v3-purple-primary' : iconColor
                  ),
                  // Hover states for icons to match button hover background
                  !iconClassName && isTransparent && 'group-hover:text-v3-purple-medium',
                  !iconClassName && isFilter && 'group-hover:text-v3-black-max',
                  !iconClassName && isSecondary && 'group-hover:text-v3-purple-primary',
                  !iconClassName && isDanger && 'group-hover:text-white',
                  !iconClassName && isDangerSecondary && 'group-hover:text-v3-red-dark',
                  !iconClassName && isFloating && 'group-hover:text-white',
                  !iconClassName && isDark && 'group-hover:text-v3-black-max',
                  !iconClassName && isGold && 'group-hover:text-white',
                  !iconClassName && !isTransparent && !isFilter && !isSecondary && !isDanger && !isDangerSecondary && !isFloating && !isGold && !isDark && 'group-hover:text-white',
                  'transition-colors duration-200'
                )
              })}
            </IconWrapper>
          ) : null
        )
      )}
      <span className="whitespace-nowrap flex items-center gap-v3-xxs">
        {loading && (
          <DSpinner
            {...(spinnerColor
              ? { color: spinnerColor }
              : { className: spinnerClassName || (isSecondary ? 'text-v3-black-secondary' : 'text-white') }
            )}
            style={{ height: 20, width: 20 }}
          />
        )}
      {loading ? (loadingLabel || label) : label}
    </span>
      {!loading && afterIcon && (
        isIconUrl(afterIcon) ? (
          <img src={afterIcon} className="flex-shrink-0 w-5 h-5" alt="icon" />
        ) : (
          typeof afterIcon === 'function' ? (
            <IconWrapper size={20}>
              {React.createElement(afterIcon, {
                className: clsx(
                  iconClassName || (
                    isDanger ? 'text-white' :
                    isDangerSecondary ? 'text-v3-red-dark' :
                    isSuccess ? 'text-white' :
                    isFloating ? 'text-white' :
                    isDark ? 'text-white' :
                    isGold ? 'text-white' :
                    isFilter ? 'text-v3-black-max' :
                    isTransparent || isSecondary ? 'text-v3-purple-primary' : iconColor
                  ),
                  // Hover states for icons to match button hover background
                  !iconClassName && isTransparent && 'group-hover:text-v3-purple-medium',
                  !iconClassName && isFilter && 'group-hover:text-v3-black-max',
                  !iconClassName && isSecondary && 'group-hover:text-v3-purple-primary',
                  !iconClassName && isDanger && 'group-hover:text-white',
                  !iconClassName && isDangerSecondary && 'group-hover:text-v3-red-dark',
                  !iconClassName && isFloating && 'group-hover:text-white',
                  !iconClassName && isDark && 'group-hover:text-v3-black-max',
                  !iconClassName && isGold && 'group-hover:text-white',
                  !iconClassName && !isTransparent && !isFilter && !isSecondary && !isDanger && !isDangerSecondary && !isFloating && !isGold && !isDark && 'group-hover:text-white',
                  'transition-colors duration-200'
                )
              })}
            </IconWrapper>
          ) : null
        )
      )}
    </button>
  )
}

export default Button